import { useState, useCallback, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { useAnalytics } from "connected-components/analytics"
import { useAccountAlias } from "resources/account"
import { useResourceCurrency } from "resources/currencies"
import { useModelMetadata } from "resources/models"
import { usePositionSetMetadata } from "resources/position-sets"
import AiTeammate from "../.."
import { AI_TEAMMATE_EVENTS } from "../../analytics"
import { ToggleButton } from "./toggle-button"
import { NudgePopover } from "./nudge-popover"

// Persisted across sessions: once the user has opened chat, the nudge retires for good.
// Scoped by account so each account gets its own discoverability state.
const HAS_USED_CHAT_KEY = "ai-teammate-has-used-chat"

function readHasUsedChat(accountAlias) {
  try {
    return window.localStorage.getItem(`${HAS_USED_CHAT_KEY}-${accountAlias}`) === "1"
  } catch {
    // Storage unavailable — default to "not used" so discoverability still wins.
    return false
  }
}

function persistHasUsedChat(accountAlias) {
  try {
    window.localStorage.setItem(`${HAS_USED_CHAT_KEY}-${accountAlias}`, "1")
  } catch {
    // Ignore — storage unavailable.
  }
}

const TIMED_TWINKLE_MS = 5000

export function AiTeammateButtonContent({ size, className, context }) {
  const accountAlias = useAccountAlias()
  const [isOpen, setIsOpen] = useState(false)
  const [initialPrompt, setInitialPrompt] = useState(null)
  // hasUsedChat persists across sessions; nudgeDismissed is session-only.
  const [initialHasUsedChat] = useState(() => readHasUsedChat(accountAlias))
  const [hasUsedChat, setHasUsedChat] = useState(initialHasUsedChat)
  const [nudgeDismissed, setNudgeDismissed] = useState(false)
  // Returning users get a brief twinkle on load; false on first-use load (not yet persisted).
  const [showTimedTwinkle, setShowTimedTwinkle] = useState(initialHasUsedChat)
  const [anchorEl, setAnchorEl] = useState(null)
  const dismissTrackedRef = useRef(false)
  const nudgeShownRef = useRef(false)
  const analytics = useAnalytics()

  const {
    modelId,
    instrument,
    base,
    startDate,
    endDate,
    isPreferencesLoaded,
  } = context

  const [modelMetadata] = useModelMetadata(modelId)
  const [resourceMetadata] = usePositionSetMetadata(modelId, instrument, modelMetadata?.availability?.currentDate)
  const [benchmarkMetadata] = usePositionSetMetadata(modelId, base, modelMetadata?.availability?.currentDate)
  const [currency] = useResourceCurrency(modelId, instrument)

  const showNudge = isPreferencesLoaded && !hasUsedChat && !nudgeDismissed && !isOpen && !!anchorEl
  useEffect(() => {
    if (showNudge && !nudgeShownRef.current) {
      nudgeShownRef.current = true
      analytics.track(AI_TEAMMATE_EVENTS.NUDGE_SHOWN)
    }
  }, [showNudge, analytics])

  // setState fires in the timeout (async) — safe from the synchronous-setState-in-effect rule.
  useEffect(() => {
    if (!showTimedTwinkle) {
      return undefined
    }
    const timeoutId = setTimeout(() => setShowTimedTwinkle(false), TIMED_TWINKLE_MS)
    return () => clearTimeout(timeoutId)
  }, [showTimedTwinkle])

  const retireNudge = useCallback((reason) => {
    setNudgeDismissed(true)
    // Multiple retire paths can fire together; track only the first.
    if (!dismissTrackedRef.current) {
      dismissTrackedRef.current = true
      analytics.track(AI_TEAMMATE_EVENTS.NUDGE_DISMISSED, { reason })
    }
  }, [analytics])

  const markChatUsed = useCallback(() => {
    setHasUsedChat(true)
    persistHasUsedChat(accountAlias)
  }, [accountAlias])

  const handleToggle = useCallback(() => {
    if (!isOpen) {
      markChatUsed()
      retireNudge("opened_chat")
    } else {
      setInitialPrompt(null)
    }
    setIsOpen(prev => !prev)
  }, [isOpen, markChatUsed, retireNudge])

  const handleDrawerClose = useCallback(() => {
    setIsOpen(false)
    setInitialPrompt(null)
  }, [])

  const handleNudgeClose = useCallback(() => {
    retireNudge("dismissed")
  }, [retireNudge])

  // onMouseDown fires before ClickAwayListener's document listener — pre-empts wrong dismiss reason.
  const handleToggleMouseDown = useCallback(() => {
    if (showNudge) {
      retireNudge("opened_chat")
    }
  }, [showNudge, retireNudge])

  const handleNudgeQuestionClick = useCallback((prompt) => {
    setInitialPrompt(prompt.text)
    setIsOpen(true)
    markChatUsed()
    analytics.track(AI_TEAMMATE_EVENTS.NUDGE_QUESTION_CLICKED, { prompt_id: prompt.id })
    retireNudge("question_clicked")
  }, [markChatUsed, retireNudge, analytics])

  return (
    <>
      <span ref={setAnchorEl} className="ai-toggle-button-anchor" onMouseDown={handleToggleMouseDown}>
        <ToggleButton
          size={size}
          className={className}
          disabled={!isPreferencesLoaded}
          tooltip={`AI Teammate${!isPreferencesLoaded ? " (Loading...)" : ""}`}
          onClick={handleToggle}
          twinkle={showNudge || showTimedTwinkle}
        />
      </span>
      <NudgePopover
        open={showNudge}
        anchorEl={anchorEl}
        onClose={handleNudgeClose}
        onQuestionClick={handleNudgeQuestionClick}
      />
      <AiTeammate
        isOpen={isOpen}
        onClose={handleDrawerClose}
        initialPrompt={initialPrompt}
        modelId={modelId}
        instrument={instrument}
        base={base}
        modelMetadata={modelMetadata}
        resourceMetadata={resourceMetadata}
        benchmarkMetadata={benchmarkMetadata}
        currency={currency}
        startDate={startDate}
        endDate={endDate}
      />
    </>
  )
}

AiTeammateButtonContent.propTypes = {
  size: PropTypes.oneOf(["small", "medium"]),
  className: PropTypes.string,
  context: PropTypes.shape({
    modelId: PropTypes.string,
    instrument: PropTypes.string,
    base: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    isPreferencesLoaded: PropTypes.bool,
  }).isRequired,
}
